import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { Field, BigButton } from "@/components/Field";
import { BackLink, Panel, SelectField, TextArea, Empty } from "@/components/Bits";
import { PageHeader } from "@/components/PageHeader";
import { tipoIcon } from "@/config/equipamentos";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";

export default function SolicitarPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [f, setF] = useState({ fazenda_id: params.get("fazenda_id") || "", equipamento_ids: params.get("equipamento_id") ? [params.get("equipamento_id")] : [],
    categoria_id: "", servico_solicitado: "", problema_relatado: "", data: "" });
  const [busy, setBusy] = useState(false);
  const { data: fazendas } = useFetch("/fazendas");
  const { data: cats } = useFetch("/categorias-servico");
  const { data: equips } = useFetch(f.fazenda_id ? "/equipamentos" : null, { fazenda_id: f.fazenda_id });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (id) => setF({ ...f, equipamento_ids: f.equipamento_ids.includes(id) ? f.equipamento_ids.filter((x) => x !== id) : [...f.equipamento_ids, id] });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/ordens/solicitar", { ...f, categoria_id: f.categoria_id || null, data: f.data || null });
      toast.success(`Pedido ${data.numero} enviado! Em breve entraremos em contato.`);
      navigate(`/ordens-servico/${data.os_id}`, { replace: true });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="solicitar-page">
      <BackLink to="/ordens-servico" label="Meus pedidos" />
      <PageHeader eyebrow="Atendimento" title="Pedir atendimento" subtitle="Conte o que está acontecendo e nossa equipe vai agendar a visita." />
      <div className="space-y-5">
        <Panel>
          <div className="space-y-4">
            {fazendas?.length === 0 && <Empty>Cadastre sua fazenda primeiro em Fazendas.</Empty>}
            <SelectField label="Fazenda *" id="s-faz" required placeholder="Selecione a fazenda" value={f.fazenda_id} onChange={(e) => setF({ ...f, fazenda_id: e.target.value, equipamento_ids: [] })}
              options={(fazendas || []).map((x) => ({ value: x.fazenda_id, label: x.nome }))} data-testid="solicitar-fazenda" />
            <SelectField label="Tipo de serviço" id="s-cat" placeholder="Não sei / outro" value={f.categoria_id} onChange={set("categoria_id")}
              options={(cats || []).filter((c) => c.ativo).map((c) => ({ value: c.categoria_id, label: c.nome }))} data-testid="solicitar-categoria" />
            <TextArea label="O que você precisa? *" id="s-serv" required value={f.servico_solicitado} onChange={set("servico_solicitado")} data-testid="solicitar-servico" />
            <TextArea label="Descreva o problema" id="s-prob" value={f.problema_relatado} onChange={set("problema_relatado")} data-testid="solicitar-problema" />
            <Field label="Data preferida" id="s-data" type="date" value={f.data} onChange={set("data")} data-testid="solicitar-data" />
          </div>
        </Panel>
        {f.fazenda_id && (equips || []).length > 0 && (
          <Panel title="Equipamentos com problema (opcional)">
            <div className="grid sm:grid-cols-2 gap-2">
              {equips.map((eq) => { const I = tipoIcon(eq.tipo); const on = f.equipamento_ids.includes(eq.equipamento_id); return (
                <button type="button" key={eq.equipamento_id} onClick={() => toggle(eq.equipamento_id)} data-testid={`solicitar-equip-${eq.equipamento_id}`}
                  className={`min-h-[56px] rounded-xl border-2 px-3 flex items-center gap-3 text-left ${on ? "border-[#1E4D2B] bg-[#E8F5E9]" : "border-[#E2DDD3] bg-white"}`}>
                  <span className={`h-6 w-6 rounded-md border-2 flex items-center justify-center ${on ? "bg-[#1E4D2B] border-[#1E4D2B] text-white" : "border-[#C9C2B4]"}`}>{on && <Check className="h-4 w-4" />}</span>
                  <I className="h-5 w-5 text-[#1E4D2B]" /><span className="font-semibold truncate">{eq.titulo}</span>
                </button>); })}
            </div>
          </Panel>
        )}
        <BigButton type="submit" disabled={busy} data-testid="solicitar-submit">{busy && <Loader2 className="h-5 w-5 animate-spin" />} Enviar pedido</BigButton>
      </div>
    </form>
  );
}
